import {
  createEntityAdapter,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";
import { LEAType } from "../../../api/events";
import { LeaReportResponse, LEASubmissionsResponse } from "../../../api/lea";
import { RootState } from "../../../app/store";
import { fetchLeaReportsThunk } from "./leaThunk";

export interface LeaReportEntity extends LeaReportResponse {
  lea_type: LEAType;
}

export interface LeaReportsState {
  lea: EntityState<LeaReportEntity, LEAType>;
}

const leaAdapter = createEntityAdapter<LeaReportEntity, LEAType>({
  selectId: (record) => record.lea_type,
});

const initialState: LeaReportsState = {
  lea: leaAdapter.getInitialState({}),
};

const applyLeaPayload = (
  state: LeaReportsState,
  payload: LEASubmissionsResponse
) => {
  const leaReportsData = payload?.lea_submissions || {};
  const entities: LeaReportEntity[] = Object.entries(leaReportsData)
    .map(([lea_type, entry]) => {
      if (!entry) return null;
      return { lea_type: lea_type as LEAType, ...entry };
    })
    .filter((leaEntity): leaEntity is LeaReportEntity => leaEntity !== null);

  leaAdapter.upsertMany(state.lea, entities);
};

const leaSlice = createSlice({
  name: "leaReports",
  initialState,
  reducers: {
    clearLeaReportsState: (state) => {
      leaAdapter.removeAll(state.lea);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeaReportsThunk.pending, (state) => {
        leaAdapter.removeAll(state.lea); 
      })
      .addCase(fetchLeaReportsThunk.fulfilled, (state, action) => {
        applyLeaPayload(state, action.payload);
      });
  },
});

export const { clearLeaReportsState } = leaSlice.actions;
export const leaReportsReducer = leaSlice.reducer;

const leaSelectors = leaAdapter.getSelectors(
  (state: RootState) => state.leaReportsData.lea
);

export const selectAllLeaReports = leaSelectors.selectAll;
export const selectLeaReportByType = leaSelectors.selectById;
export const selectLeaReportsTotal = leaSelectors.selectTotal;

export const selectLeaReportsDetailsByType = (
  state: RootState,
  leaType: LEAType
) => selectLeaReportByType(state, leaType);
